import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQuery } from './createBaseQuery';

export interface IDialog {
  id: string;
}

export interface IDialogResponse {
  dialog: IDialog;
  scenario: unknown;
  game: unknown;
  batches: unknown[];
}

export const dialogsApi = createApi({
  reducerPath: 'dialogsApi',
  baseQuery: createBaseQuery(),
  tagTypes: ['Dialogs', 'Users', 'Stats'],
  keepUnusedDataFor: 3600,
  endpoints: (builder) => ({
    getResponseStatsItems: builder.query<unknown, { from: number; to: number }>({
      query: ({ from, to }) => ({
        url: '/tools/perf/response-time',
        method: 'GET',
        params: { from, to },
      }),
      providesTags: ['Stats'],
    }),

    getGuestParams: builder.query<unknown, void>({
      query: () => ({ url: '/v1/marketing/guest-params', method: 'GET' }),
    }),

    getBatchesNumber: builder.query<{ totalBatchesNumber: number; customDialogsUserBatchesNumber: number }, void>({
      query: () => ({ url: '/v1/marketing/batches-number', method: 'GET' }),
    }),

    getDialogsUsersStats: builder.query<unknown, void>({
      query: () => ({ url: '/users-dialogs-stats', method: 'GET' }),
      providesTags: ['Users'],
    }),

    createDialog: builder.mutation<unknown, { scenarioId: string }>({
      query: ({ scenarioId }) => ({
        url: '/dialogs',
        method: 'POST',
        body: { scenarioId },
      }),
      invalidatesTags: ['Dialogs'],
    }),

    getDialogSuggestions: builder.mutation<unknown, { dialogId: string; userId: string }>({
      query: ({ dialogId, userId }) => ({
        url: `/dialogs/${dialogId}/suggestions`,
        method: 'POST',
        body: { userId },
        timeout: 30000,
      }),
    }),

    continueDialog: builder.mutation<unknown, { id: string; userMessage: string }>({
      query: ({ id, userMessage }) => ({
        url: `/dialogs/${id}/continue`,
        method: 'POST',
        body: { userMessage },
        timeout: 30000,
      }),
      invalidatesTags: ['Dialogs'],
    }),

    continueDialogMedia: builder.mutation<unknown, { id: string; taskId: string }>({
      query: ({ id, taskId }) => ({
        url: `/dialogs/${id}/continue-media`,
        method: 'POST',
        body: { taskId },
        timeout: 30000,
      }),
      invalidatesTags: ['Dialogs'],
    }),

    deleteDialog: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/dialogs/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Dialogs'],
    }),

    getDialog: builder.query<IDialogResponse, string>({
      query: (id) => ({
        url: `/dialogs/${id}`,
        method: 'GET',
      }),
      providesTags: ['Dialogs'],
    }),

    getDialogByScenarioAlias: builder.query<
      IDialogResponse,
      { alias: string; scenarioId: string; characterName: string; isAdminPanel?: boolean }
    >
      ({
        query: ({ alias, scenarioId, characterName, isAdminPanel }) => ({
          url: `/dialogs/alias/${alias}`,
          method: 'GET',
          params: {
            scenarioId,
            characterName,
            isAdminPanel: isAdminPanel || undefined,
          },
        }),
        providesTags: ['Dialogs'],
      }),
  }),
});

export const {
  useGetResponseStatsItemsQuery,
  useGetGuestParamsQuery,
  useGetBatchesNumberQuery,
  useGetDialogsUsersStatsQuery,
  useCreateDialogMutation,
  useGetDialogSuggestionsMutation,
  useContinueDialogMutation,
  useContinueDialogMediaMutation,
  useDeleteDialogMutation,
  useLazyGetResponseStatsItemsQuery,
  useGetDialogQuery,
  useLazyGetDialogQuery,
  useGetDialogByScenarioAliasQuery,
  useLazyGetDialogByScenarioAliasQuery,
} = dialogsApi;
